import { useSettings, useUpdateSettings } from "../../hooks/useSettings";
import { DEFAULT_CLIENT_SETTINGS } from "@cmmd-forge/contracts/settings";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import {
  SettingResetButton,
  SettingsPageContainer,
  SettingsRow,
  SettingsSection,
} from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";

const AUTO_CREATE_TASKS_LABELS = {
  ask: "Ask each time",
  always: "Always create",
  never: "Never create",
} as const;

const PUSH_LOCAL_PROJECT_LABELS = {
  ask: "Ask each time",
  always: "Always push",
  never: "Never push",
} as const;

export function CmmdSyncPanel() {
  const settings = useSettings();
  const { updateSettings } = useUpdateSettings();
  const sync = settings.cmmdSync;
  const defaults = DEFAULT_CLIENT_SETTINGS.cmmdSync;

  return (
    <SettingsPageContainer>
      <SettingsSection title="CMMD sync">
        <SettingsRow
          {...searchableSetting("cmmd-auto-create-tasks")}
          description="When the master proposes a plan with sub-items, whether to auto-create corresponding CMMD tasks."
          resetAction={
            sync.autoCreateTasks !== defaults.autoCreateTasks ? (
              <SettingResetButton
                label="auto-create tasks"
                onClick={() =>
                  updateSettings({
                    cmmdSync: { ...sync, autoCreateTasks: defaults.autoCreateTasks },
                  })
                }
              />
            ) : null
          }
          control={
            <Select
              value={sync.autoCreateTasks}
              onValueChange={(value) => {
                if (value === "ask" || value === "always" || value === "never") {
                  updateSettings({ cmmdSync: { ...sync, autoCreateTasks: value } });
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-44" aria-label="Auto-create tasks mode">
                <SelectValue>{AUTO_CREATE_TASKS_LABELS[sync.autoCreateTasks]}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                <SelectItem hideIndicator value="ask">
                  {AUTO_CREATE_TASKS_LABELS.ask}
                </SelectItem>
                <SelectItem hideIndicator value="always">
                  {AUTO_CREATE_TASKS_LABELS.always}
                </SelectItem>
                <SelectItem hideIndicator value="never">
                  {AUTO_CREATE_TASKS_LABELS.never}
                </SelectItem>
              </SelectPopup>
            </Select>
          }
        />

        <SettingsRow
          {...searchableSetting("cmmd-push-local-project")}
          description="When a local-only thread is linked to a CMMD project, whether to push it automatically."
          resetAction={
            sync.pushLocalProject !== defaults.pushLocalProject ? (
              <SettingResetButton
                label="push local project"
                onClick={() =>
                  updateSettings({
                    cmmdSync: { ...sync, pushLocalProject: defaults.pushLocalProject },
                  })
                }
              />
            ) : null
          }
          control={
            <Select
              value={sync.pushLocalProject}
              onValueChange={(value) => {
                if (value === "ask" || value === "always" || value === "never") {
                  updateSettings({ cmmdSync: { ...sync, pushLocalProject: value } });
                }
              }}
            >
              <SelectTrigger className="w-full sm:w-44" aria-label="Push local project mode">
                <SelectValue>{PUSH_LOCAL_PROJECT_LABELS[sync.pushLocalProject]}</SelectValue>
              </SelectTrigger>
              <SelectPopup align="end" alignItemWithTrigger={false}>
                <SelectItem hideIndicator value="ask">
                  {PUSH_LOCAL_PROJECT_LABELS.ask}
                </SelectItem>
                <SelectItem hideIndicator value="always">
                  {PUSH_LOCAL_PROJECT_LABELS.always}
                </SelectItem>
                <SelectItem hideIndicator value="never">
                  {PUSH_LOCAL_PROJECT_LABELS.never}
                </SelectItem>
              </SelectPopup>
            </Select>
          }
        />

        <SettingsRow
          {...searchableSetting("cmmd-sync-task-status")}
          description="Automatically update CMMD task status when a worker transitions (Running, In Review, Done)."
          resetAction={
            sync.autoSyncTaskStatus !== defaults.autoSyncTaskStatus ? (
              <SettingResetButton
                label="sync task status"
                onClick={() =>
                  updateSettings({
                    cmmdSync: { ...sync, autoSyncTaskStatus: defaults.autoSyncTaskStatus },
                  })
                }
              />
            ) : null
          }
          control={
            <Switch
              checked={sync.autoSyncTaskStatus}
              onCheckedChange={(checked) =>
                updateSettings({ cmmdSync: { ...sync, autoSyncTaskStatus: Boolean(checked) } })
              }
              aria-label="Sync task status automatically"
            />
          }
        />
      </SettingsSection>
    </SettingsPageContainer>
  );
}
